import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, Search } from 'lucide-react';
import { cn } from '../../utils/cn';
import { fetchCountryCodes, type CountryCode } from '../../services/countryCodesApi';
import type { PhoneValue } from './phoneUtils';

interface PhoneInputProps {
  id?: string;
  value: PhoneValue;
  onChange: (value: PhoneValue) => void;
  hasError?: boolean;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
}

const FALLBACK_COUNTRIES: CountryCode[] = [
  { name: 'India', iso_code: 'IN', dial_code: '+91' },
  { name: 'United States', iso_code: 'US', dial_code: '+1' },
  { name: 'United Kingdom', iso_code: 'GB', dial_code: '+44' },
  { name: 'Canada', iso_code: 'CA', dial_code: '+1' },
  { name: 'Australia', iso_code: 'AU', dial_code: '+61' },
  { name: 'Singapore', iso_code: 'SG', dial_code: '+65' },
  { name: 'United Arab Emirates', iso_code: 'AE', dial_code: '+971' },
  { name: 'Germany', iso_code: 'DE', dial_code: '+49' },
];

let cachedCountries: CountryCode[] | null = null;
let inflight: Promise<CountryCode[]> | null = null;

async function loadCountries(): Promise<CountryCode[]> {
  if (cachedCountries) return cachedCountries;
  if (inflight) return inflight;
  inflight = fetchCountryCodes()
    .then((res) => {
      const list = res.success && res.countries.length > 0 ? res.countries : FALLBACK_COUNTRIES;
      cachedCountries = list;
      return list;
    })
    .finally(() => {
      inflight = null;
    });
  return inflight;
}

export const PhoneInput: React.FC<PhoneInputProps> = ({
  id,
  value,
  onChange,
  hasError,
  disabled,
  placeholder = '9876543210',
  className,
}) => {
  const [countries, setCountries] = useState<CountryCode[]>(FALLBACK_COUNTRIES);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let mounted = true;
    loadCountries().then((list) => {
      if (mounted) setCountries(list);
    });
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return countries;
    return countries.filter((c) => {
      const dial = c.dial_code.replace('+', '');
      const query = q.replace('+', '');
      return (
        c.name.toLowerCase().includes(q) ||
        c.iso_code.toLowerCase() === q ||
        dial.startsWith(query)
      );
    });
  }, [countries, search]);

  // Prefer matching by ISO code so that shared dial codes (e.g. US and CA both
  // use +1) render the actual country the user picked. Fall back to dial code
  // when the caller only knows the code (e.g. an initial value with no ISO).
  const selected =
    (value.isoCode &&
      (countries.find((c) => c.iso_code === value.isoCode) ??
        FALLBACK_COUNTRIES.find((c) => c.iso_code === value.isoCode))) ||
    countries.find((c) => c.dial_code === value.dialCode) ||
    FALLBACK_COUNTRIES.find((c) => c.dial_code === value.dialCode) ||
    FALLBACK_COUNTRIES[0];

  const handleNumberChange = (raw: string) => {
    const cleaned = raw.replace(/[^\d\s-]/g, '');
    onChange({ dialCode: value.dialCode, isoCode: value.isoCode, number: cleaned });
  };

  const selectCountry = (c: CountryCode) => {
    onChange({ dialCode: c.dial_code, isoCode: c.iso_code, number: value.number });
    setOpen(false);
    setSearch('');
  };

  return (
    <div
      ref={wrapperRef}
      className={cn(
        'relative flex rounded-lg border overflow-visible bg-white transition-all',
        hasError
          ? 'border-red-400 focus-within:border-red-500 focus-within:ring-2 focus-within:ring-red-500/20'
          : 'border-navy-200 focus-within:border-accent-500 focus-within:ring-2 focus-within:ring-accent-500/20',
        disabled && 'opacity-60 cursor-not-allowed',
        className,
      )}
    >
      <button
        type="button"
        onClick={() => !disabled && setOpen((o) => !o)}
        disabled={disabled}
        className={cn(
          'flex items-center gap-1 px-3 border-r border-navy-200 text-navy-800 font-medium text-sm rounded-l-lg hover:bg-navy-50 transition-colors',
          disabled && 'cursor-not-allowed',
        )}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span>{selected.iso_code}</span>
        <span className="text-navy-600">{selected.dial_code}</span>
        <ChevronDown className={cn('w-4 h-4 text-navy-400 transition-transform', open && 'rotate-180')} />
      </button>

      <input
        id={id}
        type="tel"
        inputMode="tel"
        value={value.number}
        onChange={(e) => handleNumberChange(e.target.value)}
        disabled={disabled}
        placeholder={placeholder}
        className="flex-1 px-4 py-3 bg-transparent outline-none text-navy-900 placeholder-navy-400 rounded-r-lg"
      />

      {open && (
        <div className="absolute top-full left-0 mt-1 w-72 max-h-72 bg-white border border-navy-200 rounded-lg shadow-xl z-50 overflow-hidden flex flex-col">
          <div className="p-2 border-b border-navy-100">
            <div className="relative">
              <Search className="w-4 h-4 text-navy-400 absolute left-2 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                autoFocus
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search country or +code"
                className="w-full pl-8 pr-2 py-2 text-sm border border-navy-200 rounded-md outline-none focus:border-accent-500"
              />
            </div>
          </div>
          <ul className="overflow-y-auto flex-1" role="listbox">
            {filtered.length === 0 ? (
              <li className="px-4 py-3 text-sm text-navy-500">No matches</li>
            ) : (
              filtered.map((c) => (
                <li key={`${c.iso_code}-${c.dial_code}`}>
                  <button
                    type="button"
                    onClick={() => selectCountry(c)}
                    className={cn(
                      'w-full flex items-center justify-between gap-3 px-3 py-2 text-left text-sm hover:bg-navy-50 transition-colors',
                      c.iso_code === selected.iso_code && 'bg-accent-50 text-accent-700',
                    )}
                  >
                    <span className="truncate">
                      <span className="font-medium text-navy-800 mr-2">{c.iso_code}</span>
                      <span className="text-navy-600">{c.name}</span>
                    </span>
                    <span className="text-navy-500 font-mono text-xs">{c.dial_code}</span>
                  </button>
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  );
};
